import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { motion, AnimatePresence } from 'framer-motion';
import Image from 'next/image';
import { useLocale, useTranslations } from 'next-intl';
import Link from 'next/link';

import UsageIndicator from './usage-indicator';
import TempleKeeperBadge from './temple-keeper-badge';
import PremiumCelebration from './premium-celebration';
import QuestionForm from './question-form';
import TempleSignupCTA from './temple-signup-cta';
import { usageMetadataPropType } from './usage-indicator.types';

/**
 * Displays the oracle answer with animation flair.
 * Note: Explanation (Luna's initial message) has been removed - only deeper insight is shown.
 */
function AnswerReveal({ answer, explanation, confidence }) {
  if (!answer) {
    return null;
  }

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={answer}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ duration: 0.25 }}
        className="space-y-3"
        aria-live="polite"
      >
        {answer && (
          <div className="relative flex items-center justify-center mt-4 mb-3 min-h-[90px]">
            <motion.span
              className="text-4xl md:text-5xl font-bold text-[#FBFBFE] px-6 py-2"
              style={{
                fontFamily: 'var(--font-marcellus)',
                textShadow: '0 0 32px #D4B6FF, 0 0 64px #A45DE8, 0 0 8px #fff',
              }}
            >
              {answer}
            </motion.span>
          </div>
        )}

        {typeof confidence === 'number' && answer && (
          <p className="text-center text-sm text-gray-300">
            Confidence: {Math.round(confidence * 100)}%
          </p>
        )}

        {/* Explanation removed - only deeper insight is shown now */}
      </motion.div>
    </AnimatePresence>
  );
}

AnswerReveal.propTypes = {
  answer: PropTypes.string,
  explanation: PropTypes.string,
  confidence: PropTypes.number,
};

AnswerReveal.defaultProps = {
  answer: null,
  explanation: '',
  confidence: null,
};

/**
 * Main reusable yes/no oracle widget for free & premium tiers.
 */
function YesNoOracleWidget({
  question,
  onQuestionChange,
  onSubmit,
  isLoading,
  isDisabled,
  answer,
  explanation,
  deeperInsight,
  confidence,
  usageMetadata,
  isPremium,
  celebrate,
  onLimitReached,
  onUpgradeIntent,
  error,
  submitLabel,
  loadingLabel,
  placeholderText,
}) {
  const t = useTranslations('oracle.widget');
  const locale = useLocale();
  const numberFormatter = useMemo(() => new Intl.NumberFormat(locale), [locale]);
  const [hasShownCelebrate, setHasShownCelebrate] = useState(false);
  const [ctaDismissedForRemaining, setCtaDismissedForRemaining] = useState(null);
  const [showDeeperInsight, setShowDeeperInsight] = useState(false);
  const dailyUsesRemaining = typeof usageMetadata?.remaining === 'number' ? usageMetadata.remaining : null;
  
  // Always show deeper insight if available (no truncation)
  const shouldRenderDeeperInsight = Boolean(deeperInsight);
  
  // Show deeper insight with delay after answer is revealed (creates suspense)
  useEffect(() => {
    if (answer && deeperInsight && !showDeeperInsight) {
      // Reset when answer changes
      setShowDeeperInsight(false);
      
      // Show deeper insight after 1.5 seconds delay
      const timer = setTimeout(() => {
        setShowDeeperInsight(true);
      }, 1500);
      
      return () => clearTimeout(timer);
    } else if (!answer) {
      // Reset when answer is cleared
      setShowDeeperInsight(false);
    }
  }, [answer, deeperInsight, showDeeperInsight]);
  
  // Determine question number for CTA variant
  // Q1: remaining = 1 (used 1), Q2: remaining = 0 (used 2 - limit reached)
  const getQuestionVariant = () => {
    if (isPremium) return null; // No CTA for premium users
    if (typeof dailyUsesRemaining === 'number') {
      if (dailyUsesRemaining === 1) return 'q1'; // First question
      if (dailyUsesRemaining === 0) return 'q2'; // Second question (limit reached)
    }
    return null;
  };
  
  const questionVariant = getQuestionVariant();
  
  // Reset CTA dismissal when remaining changes (new question asked)
  useEffect(() => {
    if (typeof dailyUsesRemaining === 'number' && ctaDismissedForRemaining !== dailyUsesRemaining) {
      // Don't reset if we're dismissing for the same remaining count
      // This allows dismissing and it stays dismissed for that question
    }
  }, [dailyUsesRemaining, ctaDismissedForRemaining]);

  // Note: hasShownCelebrate is managed by the PremiumCelebration onDismiss callback
  // We don't set it here to allow the celebration to render when celebrate becomes true
  const isCelebrationVisible = Boolean(celebrate && !hasShownCelebrate);
  const limitForDescription = usageMetadata?.limit ?? 2;
  const formattedLimit = numberFormatter.format(limitForDescription);
  const freeTierLabel = t('freeTierLabel');
  const freeTierDescription = t('freeTierDescription', { limit: formattedLimit });
  const premiumCelebrationMessage = t('premiumCelebration.message');
  const premiumCelebrationSubcopy = t('premiumCelebration.subcopy');
  const deeperInsightTitle = t('deeperInsightTitle', { defaultValue: 'Deeper Insight' });
  const deeperInsightCta = t('deeperInsightCta', { defaultValue: 'Unlock full insight' });
  const deeperInsightFooter = t('deeperInsightFooter', { defaultValue: "Premium members get Luna's full guidance" });

  return (
    <div
      className="w-full bg-[#100e23]/70 rounded-xl border border-[#6A3683]/40 shadow-xl p-6 space-y-4"
      data-testid="yes-no-oracle-widget"
    >
      {/* Free tier info - title is now in HeroSection wrapper */}
      {!isPremium && (
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between mb-2">
          <div className="text-center sm:text-left text-[#D4B6FF]">
            <p className="text-xs uppercase tracking-[0.25em]">{freeTierLabel}</p>
            <p className="text-sm text-[#D4B6FF]/80">{freeTierDescription}</p>
            <Link
              href={`/${locale}/pricing`}
              className="mt-2 inline-flex items-center justify-center px-2.5 py-1 text-[10px] font-semibold text-[#FBFBFE] border border-[#6A3683] rounded-md hover:border-[#A45DE8] hover:bg-[#6A3683]/20 transition-all duration-300 w-fit silver-wave-button"
            >
              {t('freeTierCta', { defaultValue: 'Get unlimited readings' })}
            </Link>
          </div>
          <UsageIndicator usageMetadata={usageMetadata} size="sm" />
        </div>
      )}

      {/* Usage indicator for premium users */}
      {isPremium && (
        <div className="flex justify-center mb-2">
          <UsageIndicator usageMetadata={usageMetadata} size="sm" />
        </div>
      )}

      <PremiumCelebration
        isVisible={isCelebrationVisible}
        variant="inline"
        message={premiumCelebrationMessage}
        subcopy={premiumCelebrationSubcopy}
        onDismiss={() => setHasShownCelebrate(true)}
        confettiProps={{ numberOfPieces: 150, recycle: false }}
        data-testid="premium-celebration"
      />

      <QuestionForm
        value={question}
        onChange={onQuestionChange}
        onSubmit={(value) => {
          if (usageMetadata && typeof usageMetadata.remaining === 'number' && usageMetadata.remaining === 0) {
            if (onLimitReached) {
              onLimitReached();
            }
            if (onUpgradeIntent) {
              onUpgradeIntent('limit-reached');
            }
            return;
          }
          onSubmit(value);
        }}
        isLoading={isLoading}
        isDisabled={isDisabled}
        submitLabel={submitLabel}
        loadingLabel={loadingLabel}
        placeholder={placeholderText}
      />

      {error && (
        <p className="text-sm text-center text-red-400" role="alert">
          {error}
        </p>
      )}

      <AnswerReveal answer={answer} explanation={explanation} confidence={confidence} />

      {shouldRenderDeeperInsight && (
        <AnimatePresence>
          {showDeeperInsight && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            >
              <div
                className="bg-[#1a182c] border border-[#A45DE8]/50 text-gray-100 rounded-lg p-4 mt-4"
                data-testid="deeper-insight-card"
              >
                <div className="flex items-center gap-3 mb-2">
                  <Image
                    src="/images/oracle-luna.png"
                    alt="Oracle Luna"
                    width={36}
                    height={36}
                    className="rounded-full shrink-0 border-2 border-[#A45DE8]/60 shadow-[0_0_20px_rgba(164,93,232,0.5)]"
                  />
                  <h4 className="text-sm font-semibold text-[#D4B6FF]">{deeperInsightTitle}</h4>
                </div>
                <p className="text-sm leading-relaxed text-gray-200">{deeperInsight}</p>
              </div>
              
              {/* Show Temple CTA after deeper insight is revealed */}
              {questionVariant && !ctaDismissedForRemaining && answer && (
                <TempleSignupCTA
                  variant={questionVariant}
                  onSkip={() => setCtaDismissedForRemaining(dailyUsesRemaining)}
                  readingData={{
                    readingType: 'yes-no',
                    question,
                    answer,
                    reframed: explanation || '',
                    explanation: explanation || '',
                    deeperInsight: deeperInsight || '',
                    confidence: typeof confidence === 'number' ? confidence : null,
                    locale,
                  }}
                  isPremium={isPremium}
                />
              )}
            </motion.div>
          )}
        </AnimatePresence>
      )}

    </div>
  );
}

YesNoOracleWidget.propTypes = {
  question: PropTypes.string.isRequired,
  onQuestionChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  isDisabled: PropTypes.bool,
  answer: PropTypes.string,
  explanation: PropTypes.string,
  deeperInsight: PropTypes.string,
  confidence: PropTypes.number,
  usageMetadata: PropTypes.oneOfType([
    usageMetadataPropType,
    PropTypes.oneOf([null]),
  ]),
  isPremium: PropTypes.bool,
  celebrate: PropTypes.bool,
  onLimitReached: PropTypes.func,
  onUpgradeIntent: PropTypes.func,
  error: PropTypes.string,
  submitLabel: PropTypes.string,
  loadingLabel: PropTypes.string,
  placeholderText: PropTypes.string,
};

YesNoOracleWidget.defaultProps = {
  isLoading: false,
  isDisabled: false,
  answer: null,
  explanation: '',
  deeperInsight: '',
  confidence: null,
  usageMetadata: null,
  isPremium: false,
  celebrate: false,
  onLimitReached: null,
  onUpgradeIntent: null,
  error: '',
  submitLabel: 'Reveal Oracle Whisper',
  loadingLabel: 'Consulting the Oracle…',
  placeholderText: undefined,
};

export default YesNoOracleWidget;
